/* import __COLOCATED_TEMPLATE__ from './fin.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type ContentImportService from 'embercom/services/content-import-service';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { SideSheetKeys } from 'embercom/objects/workflows/graph-editor/editor-state';
import Fin from 'embercom/models/operator/visual-builder/step/fin';
import type AiAgentQuickReplies from 'embercom/models/operator-settings/ai-agent-quick-replies';
import { type ComponentLike } from '@glint/template';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import { QUICK_REPLY_ID } from 'embercom/lib/operator/resolution-bot/constants';
import UserAgentDetector from '@intercom/pulse/lib/user-agent-detector';
import type FinTrialService from 'embercom/services/fin-trial-service';
import { AiAnswersState, selectedAgentToTrialType } from 'embercom/lib/operator/fin/types';
import type Router from '@ember/routing/router-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { action } from '@ember/object';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type AiAgentQuickRepliesService from 'embercom/services/ai-agent-quick-replies-service';
import type FinStepConfig from 'embercom/objects/visual-builder/configuration/step/fin';
import { canSeeCustomAnswers } from 'embercom/helpers/can-see-custom-answers';
import type Group from 'embercom/models/operator/visual-builder/group';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type WorkflowEditorService from 'embercom/services/workflow-editor-service';
import { State } from 'embercom/models/data/matching-system/matching-constants';
import { AGENT_OPTIONS } from 'embercom/lib/operator/resolution-bot/constants';
import type ShopifyIntegration from 'embercom/models/shopify/integration';
import type MessengerInstallation from 'embercom/services/messenger-installation';
import { get, post } from 'embercom/lib/ajax';
import { SEARCH_URL } from 'embercom/lib/content-service/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import { type ContentSearchResponse } from 'embercom/lib/content-service/search-api';

interface Arguments {
  step: Fin;
  readOnly: boolean;
  editorState: EditorState;
  stepConfig?: FinStepConfig;
  connectionPointComponent: ComponentLike<{ connectionPoint: ConnectionPoint }>;
  isTemplatePreview?: boolean;
  group: Group; // Added group argument for step deletion
}

export default class FinNodeItem extends Component<Arguments> {
  @service declare appService: {
    app: {
      id: string;
      usesResolutionsWithCustomAnswers: boolean;
      hasAnswerBot: boolean;
      aiAnswersState: AiAnswersState;
    };
  };
  @service declare contentImportService: ContentImportService;
  @service declare intl: IntlService;
  @service declare AiAgentQuickRepliesService: AiAgentQuickRepliesService;
  @service declare store: Store;
  @service declare usageLimitsService: any;
  @service declare finTrialService: FinTrialService;
  @service declare router: Router;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare attributeService: $TSFixMe;
  @service('workflow-editor-service') declare workflowEditorService: WorkflowEditorService;
  @service('messenger-installation') declare messengerInstallation: MessengerInstallation;
  @service declare contentEditorService: any;

  @tracked customAnswerCount = 0;
  @tracked aiAgentQuickReplies?: AiAgentQuickReplies;
  @tracked showDeleteFinModal = false;
  @tracked shouldAutoOpenSideSheet = false;
  @tracked shopifyShops: ShopifyIntegration[] = [];
  @tracked ecommerceProductCount: number | null = null;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.intl.maybeLoadMessengerTranslations();

    // Set the callback for the config menu to trigger the modal
    if (args.stepConfig) {
      args.stepConfig.triggerDeleteFinModalCallback = this.triggerDeleteFinModal.bind(this);
    }

    if (
      this.workflowEditorService.pendingStepId &&
      this.workflowEditorService.pendingStepId === String(this.args.step.id)
    ) {
      this.workflowEditorService.pendingStepId = null;
      this.shouldAutoOpenSideSheet = true;
    }

    this._setAiContentSummary();
    taskFor(this.loadAnswerCount).perform();
    taskFor(this.loadAiAgentQuickReplies).perform();
    taskFor(this.loadFinUsageLimit).perform();
    if (this.isFinSdr) {
      taskFor(this.loadSdrConfig).perform();
      taskFor(this.loadSalesAgentContent).perform();
    }
    if (this.isFinEcommerce) {
      taskFor(this.loadShopifyShops).perform();
      taskFor(this.loadEcommerceProductCount).perform();
    }
  }

  get sideSheetKeys() {
    return SideSheetKeys;
  }

  get canUseAiAnswers() {
    return Fin.canUseAiAnswers(this.appService.app);
  }

  get canUseCustomAnswers(): boolean {
    return canSeeCustomAnswers(this.appService.app, this.customerService);
  }

  get isPerAgentTrialCustomer(): boolean {
    let trialType = selectedAgentToTrialType(this.args.step.selectedAgentValue);
    return this.finTrialService.isAgentTrialCustomer(trialType);
  }

  get finDisabledAfterTrial() {
    return (
      !this.args.step.useAiAnswers &&
      this.finTrialService.aiAnswersState === AiAnswersState.trialExpired &&
      !this.canUseCustomAnswers
    );
  }

  get showStepInvalidState() {
    return (
      this.args.step.validations.attrs.hasAiContentReadyForFin.isInvalid ||
      this.args.step.validations.attrs.hasSdrConfigForFin.isInvalid ||
      this.args.step.validations.attrs.hasSalesAgentContentForFin.isInvalid ||
      this.args.step.validations.attrs.inactivityTimer.isInvalid ||
      this.args.step.validations.attrs.supportedContexts.isInvalid ||
      this.args.step.validations.attrs.emailSetupValid.isInvalid
    );
  }

  get showAiBorder() {
    return this.args.step.useAiAnswers && !this.showStepInvalidState;
  }

  get isOneTimeMode() {
    return this.args.step.botMode === 'one_time';
  }

  get triageBranch() {
    return this.args.step.outwardConnectionPoints.find((cp) => cp.type === 'fin_triage')!;
  }

  get triageButtonLabel() {
    if (this.args.step.botMode === 'bot_only') {
      return this.AiAgentQuickRepliesService.getLabel(
        QUICK_REPLY_ID.GET_MORE_HELP,
        null,
        this.intl.locale,
      );
    } else {
      let labelKey = this.aiAgentQuickReplies?.waitForTheTeamLabel || 'talk_to_person';
      return this.AiAgentQuickRepliesService.getLabel(
        QUICK_REPLY_ID.WAIT_FOR_THE_TEAM,
        labelKey,
        this.intl.locale,
      );
    }
  }

  get thatHelpedButtonLabel() {
    let labelKey = this.aiAgentQuickReplies?.allDoneLabel || 'all_done';
    return this.AiAgentQuickRepliesService.getLabel(
      QUICK_REPLY_ID.ALL_DONE,
      labelKey,
      this.intl.locale,
    );
  }

  get buttonsLoaded(): boolean {
    return taskFor(this.loadAiAgentQuickReplies).isIdle;
  }

  get isSafari() {
    return UserAgentDetector.isSafari();
  }

  get usageLimitBreached() {
    return this.usageLimitsService.finHardLimitBreached;
  }

  get emailSetupValidationResponse() {
    return this.aiAgentSetupService.validateEmailSetup;
  }

  get hasSideSheet() {
    return true;
  }

  get liveGuidelines() {
    return this.aiAgentGuidelineService.guidelines.filter(
      (guideline) => !guideline.isDeleted && guideline.state === State.Live,
    );
  }

  get liveGuidelinesCount() {
    return this.liveGuidelines.length;
  }

  get categoriesCount() {
    return this.attributeService.aiCategoriesDescriptors.filterBy('isEnabled', true).length;
  }

  get escalationRules() {
    return this.store.peekAll('ai-agent/escalation-rule').filterBy('enabled', true);
  }

  get hasEscalationItems() {
    return (
      this.liveGuidelines.filter((guideline) => guideline.category === 'routing').length +
        this.escalationRules.length >
      0
    );
  }

  get isFinService() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN;
  }

  get isFinSdr() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_SDR;
  }

  get isFinEcommerce() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_ECOMMERCE;
  }

  get isFinSuccess() {
    return this.args.step.selectedAgentValue === AGENT_OPTIONS.FIN_SUCCESS;
  }

  get hasShopifyConnected() {
    return taskFor(this.loadShopifyShops).isRunning || this.shopifyShops.length > 0;
  }

  get hasNoEcommerceProducts(): boolean {
    return this.shopifyShops.length > 0 && this.ecommerceProductCount === 0;
  }

  get isMessengerInstalledForWeb() {
    return this.messengerInstallation.isInstalledForWeb;
  }

  get isEcommerceFullySetUp() {
    return this.isFinEcommerce && this.hasShopifyConnected && this.isMessengerInstalledForWeb;
  }

  get showShopifyAndMessengerWarning() {
    return this.isFinEcommerce && !this.hasShopifyConnected && !this.isMessengerInstalledForWeb;
  }

  get showShopifyOnlyWarning() {
    return this.isFinEcommerce && !this.hasShopifyConnected && this.isMessengerInstalledForWeb;
  }

  get showMessengerOnlyWarning() {
    return this.isFinEcommerce && this.hasShopifyConnected && !this.isMessengerInstalledForWeb;
  }

  get isChatWorkflow() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    return channels.length === 0 || channels.includes('web');
  }

  get shouldShowActionItemsDisabled() {
    return this.usageLimitBreached || this.args.step.validations.attrs.emailSetupValid.isInvalid;
  }

  get hasContentForFin() {
    return this.args.step.validations.attrs.hasAiContentReadyForFin.isValid;
  }

  get hasSdrConfigForStep() {
    return this.args.step.hasSdrConfig !== false;
  }

  get hasSalesAgentContentForStep() {
    return this.args.step.hasSalesAgentContent !== false;
  }

  get hasInvalidEmailSetup() {
    return this.args.step.validations.attrs.emailSetupValid.isInvalid;
  }

  @task({ restartable: true })
  *loadAnswerCount(): TaskGenerator<void> {
    let customAnswers = yield this.store.findAll('custom-answers/custom-answer', { reload: true });
    this.customAnswerCount = customAnswers.filter((answer: any) => answer.status === 'live').length;
    this.args.step.customAnswerCount = this.customAnswerCount;
  }

  @task({ restartable: true })
  *loadAiAgentQuickReplies(): TaskGenerator<void> {
    this.aiAgentQuickReplies = yield this.store.findRecord(
      'operator-settings/ai-agent-quick-replies',
      this.appService.app.id,
    );
  }

  @task({ restartable: true })
  *loadFinUsageLimit(): TaskGenerator<void> {
    yield this.usageLimitsService.fetchFinUsageLimits();
  }

  @task({ restartable: true })
  *loadShopifyShops(): TaskGenerator<void> {
    let shops: ShopifyIntegration[] = yield this.store.findAll('shopify/integration');
    this.shopifyShops = shops.toArray();
  }

  @task({ restartable: true })
  *loadEcommerceProductCount(): TaskGenerator<void> {
    try {
      let response: ContentSearchResponse = yield post(SEARCH_URL, {
        app_id: this.appService.app.id,
        object_types: [EntityType.EcommerceProduct],
        per_page: 0,
      });
      this.ecommerceProductCount = response.total_count ?? 0;
    } catch {
      this.ecommerceProductCount = null;
    }
  }

  @task({ restartable: true })
  *loadSdrConfig(): TaskGenerator<void> {
    let appId = this.appService.app.id;
    try {
      let config = yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`);
      let routingOutcomes = (config as { routing_outcomes?: unknown[] })?.routing_outcomes ?? [];
      this.args.step.hasSdrConfig = routingOutcomes.length > 0;
    } catch {
      this.args.step.hasSdrConfig = false;
    }
  }

  @task({ restartable: true })
  *loadSalesAgentContent(): TaskGenerator<void> {
    let appId = this.appService.app.id;
    try {
      let summary = yield get('/ember/knowledge_base/overview/usage_summary', {
        app_id: appId,
      });
      let entries: Array<{ sales_agent?: number }> = Object.values(summary);
      this.args.step.hasSalesAgentContent = entries.some((entry) => (entry.sales_agent ?? 0) > 0);
    } catch {
      this.args.step.hasSalesAgentContent = false;
    }
  }

  @action
  onSelectAgent(agentValue: string) {
    this.args.step.selectedAgent = agentValue;
    if (agentValue === AGENT_OPTIONS.FIN_SDR) {
      taskFor(this.loadSdrConfig).perform();
      taskFor(this.loadSalesAgentContent).perform();
    }
    if (agentValue === AGENT_OPTIONS.FIN_ECOMMERCE) {
      taskFor(this.loadShopifyShops).perform();
      taskFor(this.loadEcommerceProductCount).perform();
    }
  }

  @action
  openLinkInNewTab(urlName: string, event: Event) {
    event.stopPropagation();
    let url = this.router.urlFor(urlName);
    safeWindowOpen(url, '_blank');
  }

  @action
  triggerDeleteFinModal() {
    this.showDeleteFinModal = true;
  }

  @action
  hideDeleteFinModal() {
    this.showDeleteFinModal = false;
  }

  @action
  deleteFinStep() {
    this.args.editorState.deleteStep?.(this.args.step, this.args.group);
    this.showDeleteFinModal = false;
  }

  async _setAiContentSummary() {
    if (!Object.values(this.contentImportService.aiContentLibrarySummary).length) {
      await this.contentImportService.fetchAiContentLibrarySummary();
    }
    this.args.step.aiContentLibrarySummary = this.contentImportService.aiContentLibrarySummary;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin': typeof FinNodeItem;
  }
}
